import { Collapseable } from "components/UI/Collapseable/Collapseable";
import { useGeneralForm } from "components/UI/GeneralForm/hooks/useGeneralForm";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { FC } from "react";
import { useFieldArray } from "react-hook-form";
import { RequestMethod } from "types/enums";

import { FormData } from "./formSchema";
import { RequestBodyForm } from "./RequestBodyForm";
import { RequestHeadersForm } from "./RequestHeadersForm";
import { ResponseConditionsForm } from "./ResponseConditionsForm";
import { UrlParametersForm } from "./UrlParametersForm";

import FormFieldData = App.Data.FormFieldData;

interface ApiMethodFormProps {
    leadFields: Array<FormFieldData | string>;
    name: number; // index of the method in the methods array
}

export const ApiMethodForm: FC<ApiMethodFormProps> = ({ leadFields, name: parentName }) => {
    const { t } = usePaldockTranslation("ui.connection-creator-editor");
    const generalFormUtils = useGeneralForm<FormData>();
    const { control, watch } = generalFormUtils;

    const methods = watch("methods");
    const currentMethod = methods?.[parentName];
    const requestMethod = currentMethod?.requestConfig?.method;

    const showRequestBody =
        requestMethod === RequestMethod.Post ||
        requestMethod === RequestMethod.Put ||
        requestMethod === RequestMethod.Patch;

    // Field arrays paths
    const urlParametersPath = `methods.${parentName}.requestConfig.requestConnectionPropertiesUrl` as const;
    const headersPath = `methods.${parentName}.requestConfig.headers` as const;
    const bodyPath = `methods.${parentName}.requestConfig.requestConnectionProperties` as const;

    const {
        fields: urlParametersFields,
        append: appendUrlParam,
        remove: removeUrlParam,
    } = useFieldArray({
        control,
        name: urlParametersPath,
    });

    const {
        fields: headersFields,
        append: appendHeader,
        remove: removeHeader,
    } = useFieldArray({
        control,
        name: headersPath,
    });

    const {
        fields: bodyFields,
        append: appendBodyField,
        remove: removeBodyField,
    } = useFieldArray({
        control,
        name: bodyPath,
    });

    return (
        <>
            <Collapseable
                withAddItem
                title={t("sections.url_parameters")}
                handleAddItem={() => appendUrlParam({ value: "", accessor: "" })}
                className="bg-neutral-200"
            >
                {urlParametersFields.map((field, index) => (
                    <UrlParametersForm
                        key={field.id}
                        name={index}
                        parentName={parentName}
                        handleRemove={() => removeUrlParam(index)}
                        leadFields={leadFields}
                    />
                ))}
            </Collapseable>

            <Collapseable
                withAddItem
                title={t("sections.headers")}
                handleAddItem={() => appendHeader({ key: "", value: "" })}
                className="bg-neutral-200"
            >
                {headersFields.map((field, index) => (
                    <RequestHeadersForm
                        key={field.id}
                        name={index}
                        parentName={parentName}
                        handleRemove={() => removeHeader(index)}
                    />
                ))}
            </Collapseable>

            {showRequestBody && (
                <Collapseable
                    withAddItem
                    title={t("sections.request_body")}
                    handleAddItem={() => appendBodyField({ value: "", accessor: "" })}
                    className="bg-neutral-200"
                >
                    {bodyFields.map((field, index) => (
                        <RequestBodyForm
                            key={field.id}
                            name={index}
                            parentName={parentName}
                            handleRemove={() => removeBodyField(index)}
                            leadFields={leadFields}
                        />
                    ))}
                </Collapseable>
            )}

            <ResponseConditionsForm name={parentName} />
        </>
    );
};
